<template>
    <section class="header container--row container__col-md-12">
        <div class="header__details">
            <div class="merchant__details">
                <div v-if="showMerchantInitial || logoFailed" class="custom__logo">
                    <p>{{ merchantInitial }}</p>
                </div>
                <div v-else class="logo--wrapper">
                    <img
                        class="merchant__details-logo"
                        :src="merchant.logo"
                        :alt="merchant.name"
                        loading="lazy"
                        decoding="async"
                        @error="logoFailed = true"
                    />
                </div>
                <div class="merchant__details-text">
                    <p class="merchant__details-name">{{ merchant.name }}</p>
                    <p class="customer__details-email" :style="emailWidthStyling">
                        {{ customer.email }}
                    </p>
                </div>
            </div>
            <div class="customer__details">
                <div class="customer__details-money">
                    <span v-if="!state?.isAmountUpdateLoading">{{ currency }}</span>
                    <p v-if="!state?.isAmountUpdateLoading">
                        {{ formatMoney(resolvedAmount) }}
                    </p>
                    <div v-else class="loader-icon-sm"></div>
                </div>
                <div v-if="state?.info?.feeData && !state?.info?.dccAvailable" class="customer__details-fee">
                    <p class="customer__details-info">{{ $t("ui.transaction.breakdown") }}</p>
                    <svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_10848_146310)">
                            <path d="M6.99967 11.6041C9.81863 11.6041 12.1038 9.31893 12.1038 6.49998C12.1038 3.68103 9.81863 1.39581 6.99967 1.39581C4.18072 1.39581 1.89551 3.68103 1.89551 6.49998C1.89551 9.31893 4.18072 11.6041 6.99967 11.6041Z" stroke="#4F4F4F" stroke-width="1.02083" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M7 3.44848V3.4375" stroke="#4F4F4F" stroke-width="1.02083" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M7 5.47919V9.56252" stroke="#4F4F4F" stroke-width="1.02083" stroke-linecap="round" stroke-linejoin="round"/>
                        </g>
                        <defs>
                            <clipPath id="clip0_10848_146310">
                                <rect width="12.25" height="12.25" fill="white" transform="translate(0.875 0.375)"/>
                            </clipPath>
                        </defs>
                    </svg>


                    <!-- transaction breakdown -->
                    <div class="fee__breakdown">
                        <div class="fee__breakdown-title">
                            <h5>{{ $t("ui.breakdown.title") }}</h5>
                        </div>
                        <div 
                            v-for="(value, key, index) in dccFormatting"
                            :key="`${key}-${index}`" 
                            class="fee__breakdown-desc"
                        >
                            <p 
                                v-if="key && Number(value) > 0" class="fee__breakdown-name"
                            >
                                {{ formatDataField(key) === 'vat' ? formatDataField(key).toUpperCase() : formatDataField(key) }}
                            </p> 
                            <p 
                                v-if="key && Number(value) > 0"
                            >
                                {{ parseValue(value) }}
                            </p>
                        </div>
                        <div class="fee__breakdown-total">
                            <p>{{ $t("ui.breakdown.total") }}</p>
                            <p><b>{{ currency }} {{ formatMoney(resolvedAmount) }}</b></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- <div class="customer__details">
            <div class="customer__details-money">
                <span>{{ currency }}</span>
                <p>{{ amount }}</p>
            </div>
            <p class="customer__details-email">{{ customer.email }}</p>
        </div> -->
        <!-- <div class="">
            <label for="language" class="hidden">Language</label>
            <select id="language" name="language" class="">
                <option value="English">ENGLISH</option>
            </select>
        </div> -->
        <!-- <div class="dcc__info">
            <dcc-info 
                v-if="method === 'card' && state.dccInfo" 
                :dccInfo="state.dccInfo" 
                :amount="state.amount"
            />
        </div> -->
    </section>
</template>

<script>
import { injectStore, mapState } from "redux-vuex";
import { ref, reactive, onBeforeUnmount, computed } from "vue";
import { getValueFromPath, formatMoney, isWhiteLabelled } from "@/support/helpers";
// import DccInfo from "@/components/common/dcc-info.vue";

export default {
    name: "PaymentHeader",
    // components: {
    //     DccInfo,
    // },
    props: {
        customer: {
            type: Object,
            required: true,
        },
        merchant: {
            type: Object,
            required: true,
        },

        currency: {
            type: String,
            required: true,
        },
        amount: {
            type: String,
            required: true,
        },
        method: {
            type: String,
            required: true,
        },
    },
    computed: {},
    setup(props) {
        const store = injectStore();
        const checkoutData = ref(store.getState());
        const subscription = store.subscribe(() => {
            checkoutData.value = store.getState();
        });

        const formatDataField = (field) => {
            if (!field.includes("_")) {
                return field;
            }
            else {
                return field.replace(/_/g, " ");
            }
        };

        // White-labelled sessions always show the initial, never a possibly Flutterwave-branded logo.
        const showMerchantInitial = computed(() => {
            const country = getValueFromPath(checkoutData.value, "country") || null;

            return isWhiteLabelled(props.currency, country);
        });

        // `merchant_name` is effectively always present; this only guards the
        // pathological empty case, where a blank orange disc would read as broken.
        const MERCHANT_INITIAL_FALLBACK = "?";

        const merchantInitial = computed(() => {
            const name = String(getValueFromPath(props.merchant, "name") || "").trim();

            return name ? name.charAt(0).toUpperCase() : MERCHANT_INITIAL_FALLBACK;
        });

        // Merchants can pass a dead `customizations.logo`; show the initial, not a broken image.
        const logoFailed = ref(false);

        const emailWidthStyling = computed(() => {
            const customerEmail = getValueFromPath(props.customer, "email") || "";
            return props && props.customer && customerEmail.length > 28
                ? reactive({ width: "80%" }) 
                : reactive({ width: "unset" });
        });

        const resolvedAmount = computed(() => {
            if (checkoutData.value?.campaign && checkoutData.value?.campaign?.new_amount) {
                return checkoutData.value.campaign.new_amount;
            }
            else if (checkoutData.value?.dcc && checkoutData.value?.dcc?.charge_amount) {
                return checkoutData.value.dcc.charge_amount;
            }
            else {
                return checkoutData.value?.amount;
            }
        });

        const dccFormatting = computed(() => {
            const dccObj = getValueFromPath(checkoutData.value, "info.feeData") || {};
            const discountedFee = getValueFromPath(checkoutData.value, "campaign.fee") || null;
            const discountedVat = getValueFromPath(checkoutData.value, "campaign.vat") || null;

            const orderedKeys = ["amount", "fee"];
            Object.keys(dccObj)
                .filter(key => !orderedKeys.includes(key) && key !== "charge_amount")
                .forEach(key => {
                    orderedKeys.push(key);
                });
            const reorderedDccObject = {};
            orderedKeys.forEach(key => {
                if (Object.keys(dccObj).includes(key)) {
                    if (key === "fee" && discountedFee !== null) {
                        reorderedDccObject[key] = discountedFee;
                    } 
                    else if (key === "vat" && discountedVat !== null) {
                        reorderedDccObject[key] = discountedVat;
                    }
                    else {
                        reorderedDccObject[key] = dccObj[key];
                    }
                }
            });
            return reorderedDccObject;
        });

        const parseValue = (value) => value && Number(value)?.toFixed(2);

        onBeforeUnmount(() => {
            subscription();
        });

        return {
            state: mapState({
                amount: (state) => getValueFromPath(state, "amount") || null,
                dccInfo: (state) => getValueFromPath(state, "dcc") || null,
                isAmountUpdateLoading: (state) => getValueFromPath(state, "info.amountUpdate") || false,
                info: (state) => getValueFromPath(state, "info") || null,
            }),
            formatMoney,
            formatDataField,
            showMerchantInitial,
            merchantInitial,
            logoFailed,
            emailWidthStyling,
            parseValue,
            dccFormatting,
            resolvedAmount,
        };
    },
};
</script>

<style lang="scss" scoped>
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  align-items: flex-start;
  @media screen and (max-width: $breakpoint-middle * 1px) {
    margin: 0;
    padding: 3rem 2.4rem 0 0;
  }
  @media screen and (min-width: $breakpoint-mid * 1px) {
    padding-bottom: 2rem;
    border-bottom: 0.946925px solid $border-color;
  }
  &__details {
      width: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
    @media screen and (max-width: $breakpoint-mid * 1px) {
      padding-bottom: 1.78rem;
      border-bottom: 0.946925px solid #ebebeb;
    }
  }
}

.dcc__info {
  margin: 1rem 0 0.3rem 0;
}

.merchant__details {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
  @media screen and (max-width: $breakpoint-middle * 1px) {
  }
  .logo--wrapper {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    flex-shrink: 0;
  }
  &-logo {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    border-radius: 50%;
  }
  &-text {
    min-width: 0;
    margin-left: 1.1rem;
  }
  &-name {
    display: none;
    font-size: 1.4rem;
    line-height: 1.5rem;
    color: $primary-text-color;
    font-weight: $font-normal;
    padding-bottom: 0.6rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    @include screen(menner) {
      display: block;
    }
  }
}
.customer__details {
  width: 55%;
  flex-shrink: 0;
  &-money {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    color: $secondary-color;
    @include screen(menner) {
      color: $black;
    }
    span {
      font-size: 1.4rem;
      text-align: right;
      font-weight: $font-medium;
      margin-right: 0.4rem;
      padding-bottom: 0.2rem;
      @include screen(menner) {
        /* padding-top: 0.3rem; */
        padding-bottom: 0.6rem;
        // font-weight: $font-semi-bold;
        line-height: 1.1rem;
      }
    }
    p {
      font-family: $font-primary;
      font-size: 2rem;
      line-height: 2.3rem;
      font-weight: bold;
      margin: 0;
      @include screen(menner) {
        font-weight: $font-medium;
      }
    }
  }
  &-email {
    // text-align: right;
    font-weight: $font-normal;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 1.2rem;
    line-height: 1.2rem;
    color: $secondary-text-color;
  }
  &-fee {
    display: flex;
    justify-content: flex-end;
    position: relative;
  }
  &-info {
    font-size: 12px;
    line-height: 1.2rem;
    font-weight: $font-normal;
    color: $secondary-text-color;
    margin-right: 0.3rem;
  }
}

.fee__breakdown {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;

  position: absolute;
  background-color: #4F4F4F;
  right: -26rem;
  top: -2.3rem;
  padding: 2.4rem;
  border-radius: 4px;
  z-index: 99;

  @media screen and (max-width: $breakpoint-middle * 1px) {
    margin-right: 2rem;
    right: -2rem;
    margin-top: 5rem;
    margin-top: 5rem;
  }

  &-title {
    font-family: $font-primary;
    font-weight: $font-bold;
    line-height: 23px;
    margin-bottom: 1.5rem;
    display: flex;
    justify-content: space-between;
    font-size: 2.4rem;
    color: #fff;
  }

  &-desc {
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    gap: 5rem;
    font-size: 1.4rem;
    line-height: 24px;
    color: #fff;
    margin-bottom: 0.3rem;

  }
  &-total {
    display: flex;
    justify-content: space-between;
    gap: 5rem;
    font-size: 1.4rem;
    color: #fff;
    margin-bottom: 0.5rem;
    border-top: 1px dashed #E0E0E0;
    padding-top: 1.5rem;
    margin-top: 1.5rem;
    line-height: 24px;
  }
  &-name {
    color: #E0E0E0;
    text-transform: capitalize;
  }
}

.customer__details-fee:hover .fee__breakdown {
  opacity: 1;
  visibility: visible;
}
.fee__breakdown::before {
  content: "";
  position: absolute;
  border: 6px solid transparent;
  border-left-color: #4F4F4F;
  left: -6px;
  transform: translateX(-50%) rotate(180deg);

  @media screen and (max-width: $breakpoint-middle * 1px) {
    left: unset;
    top: -12px;
    right: 10%;
    transform: translateX(-50%) rotate(270deg);
  }
}
.custom__logo {
  background-color: #FF9B00;
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;
  p {
    color: #12122C;
    font-size: 1.75rem;
    font-weight: 600;
  }
}
</style>